import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  getFeatureCountryName,
  getFeatureIso3,
  findPcpEntryForFeature,
  resolveFastReportCountry,
} from '../../utils/fastReport/countryResolver';
import { ISO3_TO_FAST_COUNTRY, ISO3_TO_PCP_COUNTRY } from '../../utils/fastReport/countryIso3';
import { COUNTRY_ZOOM_THRESHOLD } from '../../utils/fastReport/countryAnalytics';
import {
  getVaccCoverageBand,
  getVaccFillBand,
  getVaccPatternUrl,
} from './VaccinationPatternDefs';

type GeoFeature = {
  properties?: Record<string, string | number | undefined>;
};

export interface CountrySelectPayload {
  fastReportCountry: string | null;
  geoName: string;
}

interface CountryBoundariesLayerProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  geoJsonData: any;
  mapZoom: number;
  selectedCountry: string | null;
  selectedGeoName: string | null;
  showPcpStyling: boolean;
  pcpData: { Country: string; PCP_Stage: string }[];
  getPcpStageColor: (stage: string) => string;
  /** When true, fill countries by vaccination dose / coverage / flagged band (pattern hatch). */
  showVaccinationChoropleth?: boolean;
  /** Country name → total vaccine doses (selected vacc layers). Used when coverage is absent. */
  vaccinationDosesByCountry?: Record<string, number>;
  /**
   * Country name → coverage % (0–100). When set for a country, hatch bands use coverage
   * instead of absolute doses (RISP Now — diagonal lines).
   */
  vaccinationCoverageByCountry?: Record<string, number>;
  /**
   * Country name → estimated coverage % from FAST (dotted hatch).
   * Used when country is not in vaccinationCoverageByCountry.
   */
  vaccinationEstimatedCoverageByCountry?: Record<string, number>;
  /** Countries with vaccination activity but unknown coverage. */
  vaccinationFlaggedByCountry?: Set<string> | Record<string, boolean>;
  fastReportCountries: string[];
  /**
   * When set, only these FAST / geo country names (case-insensitive) stay visible.
   * Used when a region filter is active so out-of-region polygons are not interactive.
   */
  visibleCountryNames?: string[] | null;
  onCountrySelect: (payload: CountrySelectPayload) => void;
  /** When true, also renders admin_level 1 (district/province) polygons */
  showDistrictBoundaries?: boolean;
}

function isFlagged(
  country: string | null,
  flagged: Set<string> | Record<string, boolean> | undefined
): boolean {
  if (!country || !flagged) return false;
  if (flagged instanceof Set) return flagged.has(country);
  return !!flagged[country];
}

function nameKey(name: string | null | undefined): string {
  return (name || '').trim().toLowerCase();
}

const CountryBoundariesLayer: React.FC<CountryBoundariesLayerProps> = ({
  geoJsonData,
  mapZoom,
  selectedCountry,
  selectedGeoName,
  showPcpStyling,
  pcpData,
  getPcpStageColor,
  showVaccinationChoropleth = false,
  vaccinationDosesByCountry = {},
  vaccinationCoverageByCountry = {},
  vaccinationEstimatedCoverageByCountry = {},
  vaccinationFlaggedByCountry,
  fastReportCountries,
  visibleCountryNames = null,
  onCountrySelect,
  showDistrictBoundaries = false,
}) => {
  const map = useMap();
  const geoJsonLayerRef = useRef<L.GeoJSON | null>(null);
  const interactive =
    showVaccinationChoropleth ||
    showPcpStyling ||
    mapZoom >= COUNTRY_ZOOM_THRESHOLD;

  const visibleNameKeys = useMemo(() => {
    if (!visibleCountryNames || visibleCountryNames.length === 0) return null;
    return new Set(visibleCountryNames.map(nameKey).filter(Boolean));
  }, [visibleCountryNames]);

  const resolveFeature = useCallback(
    (feature?: GeoFeature) => {
      const geoName = feature ? getFeatureCountryName(feature) : '';
      const iso3 = feature ? getFeatureIso3(feature) : null;
      const resolved = geoName || iso3
        ? resolveFastReportCountry(geoName, fastReportCountries, iso3)
        : null;
      return { geoName, iso3, resolved };
    },
    [fastReportCountries]
  );

  const featureInVisibleSet = useCallback(
    (feature: GeoFeature) => {
      if (!visibleNameKeys) return true;
      const { geoName, iso3, resolved } = resolveFeature(feature);
      if (resolved && visibleNameKeys.has(nameKey(resolved))) return true;
      if (geoName && visibleNameKeys.has(nameKey(geoName))) return true;
      if (iso3) {
        const fromIso = ISO3_TO_FAST_COUNTRY[iso3] || ISO3_TO_PCP_COUNTRY[iso3];
        if (fromIso && visibleNameKeys.has(nameKey(fromIso))) return true;
      }
      return false;
    },
    [visibleNameKeys, resolveFeature]
  );

  const filteredGeoJsonData = useMemo(() => {
    if (!geoJsonData || !geoJsonData.features) return geoJsonData;

    return {
      ...geoJsonData,
      features: geoJsonData.features.filter(
        (feature: { properties?: Record<string, string | number | undefined> }) => {
          const adminLevel = feature.properties?.admin_level;
          if (adminLevel === 1 && !showDistrictBoundaries) return false;
          if (visibleNameKeys && !featureInVisibleSet(feature)) return false;
          return true;
        }
      ),
    };
  }, [geoJsonData, showDistrictBoundaries, visibleNameKeys, featureInVisibleSet]);

  const lookupDoses = useCallback(
    (geoName: string, resolved: string | null) => {
      if (resolved && vaccinationDosesByCountry[resolved] != null) {
        return vaccinationDosesByCountry[resolved];
      }
      if (geoName && vaccinationDosesByCountry[geoName] != null) {
        return vaccinationDosesByCountry[geoName];
      }
      return 0;
    },
    [vaccinationDosesByCountry]
  );

  const lookupCoverage = useCallback(
    (geoName: string, resolved: string | null): number | null => {
      if (resolved && vaccinationCoverageByCountry[resolved] != null) {
        return vaccinationCoverageByCountry[resolved];
      }
      if (geoName && vaccinationCoverageByCountry[geoName] != null) {
        return vaccinationCoverageByCountry[geoName];
      }
      return null;
    },
    [vaccinationCoverageByCountry]
  );

  const lookupEstimatedCoverage = useCallback(
    (geoName: string, resolved: string | null): number | null => {
      if (resolved && vaccinationEstimatedCoverageByCountry[resolved] != null) {
        return vaccinationEstimatedCoverageByCountry[resolved];
      }
      if (geoName && vaccinationEstimatedCoverageByCountry[geoName] != null) {
        return vaccinationEstimatedCoverageByCountry[geoName];
      }
      return null;
    },
    [vaccinationEstimatedCoverageByCountry]
  );

  const lookupFlagged = useCallback(
    (geoName: string, resolved: string | null) => {
      return isFlagged(resolved, vaccinationFlaggedByCountry) || isFlagged(geoName, vaccinationFlaggedByCountry);
    },
    [vaccinationFlaggedByCountry]
  );

  const resolveVaccStyle = useCallback(
    (geoName: string, resolved: string | null) => {
      const rispCov = lookupCoverage(geoName, resolved);
      if (rispCov != null && rispCov > 0) {
        const band = getVaccCoverageBand(rispCov);
        return band ? { band, style: 'risp' as const } : null;
      }
      const estCov = lookupEstimatedCoverage(geoName, resolved);
      if (estCov != null && estCov > 0) {
        const band = getVaccCoverageBand(estCov);
        return band ? { band, style: 'estimated' as const } : null;
      }
      const doses = lookupDoses(geoName, resolved);
      const flagged = lookupFlagged(geoName, resolved);
      const band = getVaccFillBand(doses, flagged);
      if (!band) return null;
      if (band === 'flagged') return { band, style: 'flagged' as const };
      // Historical dose bands keep line hatch
      return { band, style: 'risp' as const };
    },
    [lookupCoverage, lookupEstimatedCoverage, lookupDoses, lookupFlagged]
  );

  const hasVaccFill = useCallback(
    (geoName: string, resolved: string | null) => {
      if (!showVaccinationChoropleth) return false;
      return !!resolveVaccStyle(geoName, resolved);
    },
    [showVaccinationChoropleth, resolveVaccStyle]
  );

  const getStyle = useCallback(
    (feature?: GeoFeature) => {
      const { geoName, iso3, resolved } = resolveFeature(feature);
      const isSelected =
        (resolved && resolved === selectedCountry) ||
        geoName === selectedGeoName ||
        (selectedCountry &&
          geoName &&
          resolveFastReportCountry(geoName, [selectedCountry]) === selectedCountry);

      // PCP takes priority over vaccination when both layers are enabled
      if (showPcpStyling && feature) {
        const pcpEntry = findPcpEntryForFeature(pcpData, geoName, resolved, iso3);
        const pcpStage = pcpEntry?.PCP_Stage || '';
        return {
          fillColor: getPcpStageColor(pcpStage),
          fillOpacity: isSelected ? 0.75 : 0.6,
          color: isSelected ? '#015039' : '#ffffff',
          weight: isSelected ? 3 : 1,
        };
      }

      if (showVaccinationChoropleth && feature) {
        const fill = resolveVaccStyle(geoName, resolved);
        if (fill) {
          const isFlagOnly = fill.band === 'flagged';
          return {
            fillColor: getVaccPatternUrl(fill.band, fill.style),
            fillOpacity: isSelected ? 0.95 : 0.85,
            color: isSelected
              ? '#015039'
              : isFlagOnly
                ? '#b45309'
                : fill.style === 'estimated'
                  ? '#147a5c'
                  : '#0f766e',
            weight: isSelected ? 3 : 1.25,
            opacity: 0.9,
          };
        }
        return {
          fillColor: '#94a3b8',
          fillOpacity: isSelected ? 0.12 : 0.04,
          color: isSelected ? '#015039' : '#94a3b8',
          weight: isSelected ? 2.5 : 1,
        };
      }

      if (!interactive) {
        return {
          fillColor: '#94a3b8',
          fillOpacity: 0,
          color: '#94a3b8',
          weight: 0,
          opacity: 0,
        };
      }

      return {
        fillColor: isSelected ? '#015039' : '#10b981',
        fillOpacity: isSelected ? 0.35 : 0.08,
        color: isSelected ? '#015039' : '#64748b',
        weight: isSelected ? 3 : 1,
      };
    },
    [
      showVaccinationChoropleth,
      resolveVaccStyle,
      resolveFeature,
      showPcpStyling,
      pcpData,
      getPcpStageColor,
      selectedCountry,
      selectedGeoName,
      interactive,
    ]
  );

  useEffect(() => {
    const layer = geoJsonLayerRef.current;
    if (!layer) return;
    layer.eachLayer((l) => {
      const feature = (l as L.Layer & { feature?: GeoFeature }).feature;
      if (feature) {
        (l as L.Path).setStyle(getStyle(feature));
      }
      (l as L.Layer & { options?: { interactive?: boolean } }).options = {
        ...(l as L.Layer & { options?: { interactive?: boolean } }).options,
        interactive,
      };
    });
  }, [
    getStyle,
    interactive,
    mapZoom,
    selectedCountry,
    selectedGeoName,
    showPcpStyling,
    showVaccinationChoropleth,
    vaccinationDosesByCountry,
    vaccinationCoverageByCountry,
    vaccinationEstimatedCoverageByCountry,
    vaccinationFlaggedByCountry,
  ]);

  const onEachFeature = useCallback(
    (feature: GeoFeature, layer: L.Layer) => {
      const { geoName, iso3, resolved } = resolveFeature(feature);

      (layer as L.Layer & { options?: { interactive?: boolean } }).options = {
        ...layer.options,
        interactive,
      };

      if (showPcpStyling) {
        const pcpEntry = findPcpEntryForFeature(pcpData, geoName, resolved, iso3);
        const pcpStage = pcpEntry?.PCP_Stage || 'No data';
        const label =
          resolved ||
          (iso3
            ? ISO3_TO_FAST_COUNTRY[iso3] || ISO3_TO_PCP_COUNTRY[iso3]
            : undefined) ||
          geoName ||
          'Country';
        layer.bindPopup(
          `<div style="padding: 8px;">
            <h3 style="font-weight: bold; margin-bottom: 4px;">${label}</h3>
            <p><strong>PCP Stage:</strong> ${pcpStage}</p>
          </div>`
        );
      }

      layer.on({
        mouseover: (e) => {
          if (!interactive) return;
          // Vaccination details live in the country panel — no hover fill/popup.
          if (showVaccinationChoropleth && !showPcpStyling) return;
          const target = e.target as L.Path;
          const vaccFill = hasVaccFill(geoName, resolved);
          // Only boost fill opacity when the country already has a PCP fill.
          if (showPcpStyling || vaccFill) {
            target.setStyle({
              weight: 2.5,
              fillOpacity: 0.95,
            });
          } else {
            target.setStyle({
              weight: 2.5,
              color: '#015039',
            });
          }
          if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
            target.bringToFront();
          }
        },
        mouseout: (e) => {
          if (showVaccinationChoropleth && !showPcpStyling) return;
          const target = e.target as L.Path;
          target.setStyle(getStyle(feature));
        },
        click: (e) => {
          if (!interactive) return;
          L.DomEvent.stopPropagation(e);
          const pathLayer = e.target as L.Path;
          const bounds = (pathLayer as L.Polygon).getBounds?.();
          if (bounds?.isValid?.()) {
            // Keep a wider regional frame — details are in the side panel.
            map.flyToBounds(bounds, {
              maxZoom: 5.5,
              padding: [80, 80],
              duration: 0.45,
            });
          }
          onCountrySelect({
            fastReportCountry: resolved,
            geoName,
          });
        },
      });
    },
    [
      interactive,
      showVaccinationChoropleth,
      showPcpStyling,
      pcpData,
      hasVaccFill,
      resolveFeature,
      onCountrySelect,
      map,
      getStyle,
    ]
  );

  const layerKey = `boundaries-${filteredGeoJsonData?.features?.length || 0}-${showVaccinationChoropleth}-${showPcpStyling}-${visibleNameKeys ? visibleNameKeys.size : 'all'}`;

  return (
    <GeoJSON
      key={layerKey}
      ref={(instance) => {
        geoJsonLayerRef.current = instance;
      }}
      data={filteredGeoJsonData}
      style={(feature) => getStyle(feature ?? undefined)}
      onEachFeature={onEachFeature}
    />
  );
};

export default CountryBoundariesLayer;
